import { AlertTriangle, CalendarClock, CheckCircle2, FileClock } from 'lucide-react';
import type { AvancementFacturation as Avancement } from '../../lib/useFinancements';

const TONS: Record<Avancement['etape']['ton'], { cadre: string; icone: typeof CheckCircle2 }> = {
  success: { cadre: 'border-emerald-200 bg-emerald-50 text-emerald-800', icone: CheckCircle2 },
  warning: { cadre: 'border-amber-200 bg-amber-50 text-amber-800', icone: FileClock },
  danger: { cadre: 'border-red-200 bg-red-50 text-red-700', icone: AlertTriangle },
  info: { cadre: 'border-blue-200 bg-blue-50 text-blue-800', icone: CalendarClock },
};

function Chiffre({
  libelle,
  valeur,
  alerte = false,
}: {
  libelle: string;
  valeur: string;
  alerte?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-codialis-gray">{libelle}</dt>
      <dd
        className={`font-display text-sm font-extrabold tabular-nums ${alerte ? 'text-red-700' : 'text-codialis-navy'}`}
      >
        {valeur}
      </dd>
    </div>
  );
}

/**
 * Où en est la facturation du coût pédagogique d'un contrat (TF-415
 * rouverte) : ce qui est dû sur le parcours — le coût entier, ou sa part
 * jusqu'à la rupture —, ce qui est facturé, ce qui reste, et la prochaine
 * étape — quand facturer, et quoi. Tout vient de l'API
 * (`domain/avancement-facturation.ts`).
 */
export function AvancementFacturation({ avancement }: { avancement: Avancement }): JSX.Element {
  const { part, etape } = avancement;
  const ton = TONS[etape.ton];
  const Icone = ton.icone;

  return (
    <section aria-label="Avancement de la facturation" className="rounded-xl bg-codialis-sand px-3 py-3 sm:px-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h6 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
          Avancement de la facturation
        </h6>
        {part !== null && (
          <span className="font-display text-sm font-extrabold tabular-nums text-codialis-navy">{part} % facturé</span>
        )}
      </div>
      {part !== null && (
        <div
          role="progressbar"
          aria-label="Part du coût pédagogique facturée"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={part}
          className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white"
        >
          <div className="h-full rounded-full bg-codialis-mint" style={{ width: `${part}%` }} />
        </div>
      )}

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-4">
        <Chiffre
          libelle={avancement.nonFacturable ? 'Dû jusqu’à la rupture' : 'Dû sur le parcours'}
          valeur={avancement.du?.texte ?? '—'}
        />
        <Chiffre libelle="Facturé" valeur={avancement.facture.texte} />
        {avancement.tropFacture ? (
          <Chiffre libelle="Trop facturé" valeur={avancement.tropFacture.texte} alerte />
        ) : (
          <Chiffre libelle="Reste à facturer" valeur={avancement.reste?.texte ?? '—'} />
        )}
        {avancement.nonFacturable && (
          <Chiffre libelle="Non facturable (rupture)" valeur={avancement.nonFacturable.texte} />
        )}
      </dl>
      {avancement.detailDu && (
        <p className="mt-1.5 break-words text-[11px] text-codialis-gray">{avancement.detailDu}</p>
      )}

      <div className={`mt-3 flex gap-2 rounded-lg border px-3 py-2 ${ton.cadre}`}>
        <Icone className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs font-semibold">
            <span className="min-w-0">{etape.libelle}</span>
            {etape.montant && <span className="font-display text-sm font-extrabold tabular-nums">{etape.montant}</span>}
          </p>
          {etape.detail && <p className="mt-0.5 break-words text-[11px]">{etape.detail}</p>}
        </div>
      </div>
    </section>
  );
}
